import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { BATTLE_PASS_TIERS, passById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { useBalances } from "../state/balances.js";
import { useStore } from "./state.js";
import { passesApi } from "./api.js";
import { BundleList, Price } from "./cards.js";
import type { PassView } from "./types.js";

function PassCard({ pass, onChange }: { pass: PassView; onChange: () => void }) {
  const t = useT();
  const run = useStore((s) => s.run);
  const buy = useStore((s) => s.buy);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const def = passById(pass.id);
  const claim = async (key: number) => {
    const res = await run(() => passesApi.claim(pass.id, key), t("store.pass.claimed"));
    if (res) {
      celebrate({ key: pass.id, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      onChange();
    }
  };
  const purchase = async (premiumPlus = false) => {
    const res = await buy(pass.id, premiumPlus ? { premiumPlus } : {});
    if (res) onChange();
  };
  const claimable = pass.kind === "battle" ? [] : pass.claimable;
  return (
    <article className={`offer pass ${pass.active ? "active" : ""}`} data-testid={`pass-${pass.id}`}>
      <div className="row">
        <div className="grow">
          <div className="offer-name">{pick(pass.name)}</div>
          <div className="dim" style={{ fontSize: 11 }}>{pass.durationDays ? t("store.pass.days", { n: pass.durationDays }) : t("store.pass.untilHq")}</div>
        </div>
        {pass.active ? <span className="badge gold">{t("store.pass.active")}</span> : null}
      </div>
      {pass.active && pass.expiresAt ? (
        <div className="mono dim" style={{ fontSize: 11 }}>
          <FunctionalIcon name="clock" /> <Timer finishesAt={pass.expiresAt} />
        </div>
      ) : null}
      <ul className="perks">
        {def.perks.map((p) => (
          <li key={p}>{t(`store.perk.${p.split(":")[0]!.replace(/\W/g, "_")}`, { v: p.split(":")[1] ?? "" })}</li>
        ))}
        {def.drip ? <li>{t("store.perk.drip", { n: def.drip.creditsPerDay })}</li> : null}
        {def.payouts ? <li>{t("store.perk.payouts", { n: def.payouts.reduce((s, p) => s + (p.reward.credits ?? 0), 0) })}</li> : null}
        {def.milestones ? <li>{t("store.perk.milestones", { n: def.milestones.length })}</li> : null}
      </ul>
      {pass.dripDue.length ? (
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {pass.dripDue.map((d) => (
            <Button key={d} variant="gold" onClick={() => void claim(d)} data-testid={`pass-drip-${pass.id}-${d}`}>
              {t("store.pass.drip", { day: d + 1 })}
            </Button>
          ))}
        </div>
      ) : null}
      {claimable.length ? (
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {claimable.map((k) => (
            <Button key={k} variant="gold" onClick={() => void claim(k)} data-testid={`pass-claim-${pass.id}-${k}`}>
              {pass.kind === "growth" ? t("store.pass.payout", { hq: k }) : t("store.pass.milestone", { n: k + 1 })}
            </Button>
          ))}
        </div>
      ) : null}
      {!pass.active && pass.kind !== "battle" ? (
        <div className="row" style={{ marginTop: 6 }}>
          <span className="grow" />
          <Button variant="primary" onClick={() => void purchase()} disabled={credits < pass.price} data-testid={`buy-${pass.id}`}>
            <Price price={pass.price} currency="CREDITS" />
          </Button>
        </div>
      ) : null}
      {pass.kind === "battle" ? (
        <div className="row" style={{ marginTop: 6, gap: 6, flexWrap: "wrap" }}>
          <span className="dim" style={{ fontSize: 11 }}>{t(`store.pass.track.${pass.track}`)}</span>
          <span className="grow" />
          {pass.track === "free" ? (
            <Button variant="primary" onClick={() => void purchase()} disabled={credits < pass.price} data-testid="buy-pass.battle">
              <Price price={pass.price} currency="CREDITS" />
            </Button>
          ) : null}
          {pass.track !== "premium_plus" && pass.premiumPlus ? (
            <Button variant="gold" onClick={() => void purchase(true)} disabled={credits < pass.premiumPlus.price - (pass.track === "premium" ? pass.price : 0)} data-testid="buy-pass.battle.plus">
              {t("store.pass.plus")} <Price price={pass.premiumPlus.price - (pass.track === "premium" ? pass.price : 0)} currency="CREDITS" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/** The Battle Pass track: 50 (+10) tiers with free and premium rewards, retroactive claims, tier purchase (R65). */
export function BattlePassTrack({ pass, onChange }: { pass: PassView; onChange: () => void }) {
  const t = useT();
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const claim = async (tier: number) => {
    const res = await run(() => passesApi.claim("pass.battle", tier), t("store.pass.claimed"));
    if (res) {
      celebrate({ key: "pass.battle", granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      onChange();
    }
  };
  const buyTier = async () => {
    const res = await run(() => passesApi.buyTier(), t("store.pass.tierBought"));
    if (res) onChange();
  };
  const tiers = BATTLE_PASS_TIERS.filter((x) => x.tier <= pass.maxTier);
  const nextPoints = tiers.find((x) => x.tier === pass.tier + 1)?.points ?? tiers[tiers.length - 1]!.points;
  const prevPoints = tiers.find((x) => x.tier === pass.tier)?.points ?? 0;
  const pct = nextPoints > prevPoints ? Math.min(100, Math.round(((pass.points - prevPoints) / (nextPoints - prevPoints)) * 100)) : 100;
  return (
    <div data-testid="battle-pass">
      <div className="row" style={{ gap: 10 }}>
        <span className="badge gold">{t("store.pass.tier", { tier: pass.tier, max: pass.maxTier })}</span>
        <span className="mono dim">{pass.points} P</span>
        <span className="grow" />
        <Button onClick={() => void buyTier()} disabled={credits < pass.tierCredits || pass.tier >= pass.maxTier} data-testid="pass-buy-tier">
          {t("store.pass.buyTier", { n: pass.tierCredits })}
        </Button>
      </div>
      <div className="progress" style={{ margin: "8px 0" }}>
        <div className="progress-fill gold" style={{ width: `${pct}%` }} />
      </div>
      <div className="tier-track">
        {tiers.map((x) => {
          const reached = x.tier <= pass.tier;
          const claimed = pass.claimed.includes(x.tier);
          return (
            <div key={x.tier} className={`tier ${reached ? "reached" : ""} ${claimed ? "claimed" : ""}`} data-testid={`tier-${x.tier}`}>
              <div className="mono tier-no">{x.tier}</div>
              <BundleList bundle={x.free} compact />
              <div className={`tier-premium ${pass.track === "free" ? "locked" : ""}`}>
                <BundleList bundle={x.premium} compact />
              </div>
              {reached && !claimed ? (
                <Button variant="gold" onClick={() => void claim(x.tier)} data-testid={`tier-claim-${x.tier}`}>
                  {t("common.claim")}
                </Button>
              ) : null}
              {claimed ? <span className="dim"><FunctionalIcon name="check" /></span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PassesView() {
  const t = useT();
  const [passes, setPasses] = useState<PassView[] | null>(null);
  const reload = async () => setPasses(await passesApi.list());
  useEffect(() => {
    void reload();
  }, []);
  if (!passes) return <p className="hint">{t("common.loading")}</p>;
  const battle = passes.find((p) => p.kind === "battle");
  return (
    <>
      <Panel title={t("store.tab.passes")}>
        <div className="offers" data-testid="store-passes">
          {passes.map((p) => (
            <PassCard key={p.id} pass={p} onChange={() => void reload()} />
          ))}
        </div>
      </Panel>
      {battle ? (
        <Panel title={t("store.pass.battle")}>
          <BattlePassTrack pass={battle} onChange={() => void reload()} />
        </Panel>
      ) : null}
    </>
  );
}
